import { useEffect, useRef } from 'react';

import { useActiveElement } from '../../hooks/useActiveElement.js';
import { useFocusContext } from '../../hooks/useFocusContext.js';
import { useModality } from '../../hooks/useModality.js';
import { type DataTestId } from '../../types/data-props.js';
import type { WithChildren } from '../../types/with-children.js';
import { attemptFocus } from '../../utils/focus-management/attempt-focus.js';
import { focusFirstDescendant } from '../../utils/focus-management/focus-first-descendant.js';
import { getFirstTabbableChild } from '../../utils/focus-management/get-first-tabbable-child.js';
import { getLastTabbableChild } from '../../utils/focus-management/get-last-tabbable-child.js';
import { isInInertSubtree } from '../../utils/focus-management/is-tabbable.js';

// #region Types

/**
 * Prop definition of the `FocusScope` component.
 * @public
 */
export interface FocusScopeProps extends WithChildren, DataTestId {
  /**
   * If set to true, the focus scope automatically focuses the first focusable element inside.
   * @defaultValue false
   */
  autoFocus?: boolean;
  /**
   * If set to true, the focus scope traps the focus inside.
   * @defaultValue false
   */
  contain?: boolean;
}
// #endregion Types

/**
 * The `FocusScope` helps you manage the focus when opening any overlay. If you
 * want to create a custom overlay, you can wrap the content of your overlay in the
 * `FocusScope` to help you make the overlay accessible and handle focus
 * appropriately.
 * @public
 */
export const FocusScope = (props: FocusScopeProps) => {
  const { autoFocus, contain, children, 'data-testid': dataTestId } = props;
  const focusContext = useFocusContext();
  const modality = useModality();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const focusedElement = useActiveElement();
  const triggerElement = useRef(focusedElement);

  let focusOptions: { focusVisible: boolean } | undefined;

  // If the modality is keyboard, we want to focus the first focusable element and show the focus ring.
  if (modality === 'keyboard') {
    focusOptions = { focusVisible: true };
  } else if (
    // If the modality is pointer or touch, we want to focus the first focusable element but not show the focus ring.
    modality === 'pointer' ||
    modality === 'touch'
  ) {
    focusOptions = { focusVisible: false };
  }

  // Keep a ref so event handlers always read the current modality-derived value,
  // even when modality changes while the scope is open (effect deps don't include modality).
  const focusOptionsRef = useRef(focusOptions);
  focusOptionsRef.current = focusOptions;

  useEffect(() => {
    if (contain) {
      const triggerElementCopy = triggerElement.current;

      // Register on the active-scope stack BEFORE moving focus, so the
      // previous top scope's focusin listener sees this scope as the new top
      // and yields rather than stealing focus back.
      const scopeRegistration = {
        wrapperRef,
        triggerElement: triggerElementCopy,
      };
      const unregister = focusContext.registerScope(scopeRegistration);
      focusContext.setBackgroundInertness(true);

      // Lets the re-assert below hand focus back to whatever the scope's
      // content focused itself (e.g. a confirmation dialog focusing its
      // destructive action via a ref) rather than to the first focusable
      // element.
      let lastFocusedInScope: Element | null = null;

      // Containment listener: only the top scope acts. When focus escapes
      // into another registered scope (e.g. a closing menu restoring focus
      // to a trigger that lives in an outer Sheet), pull it back. Focus
      // moving into a sibling portal that isn't a FocusScope (e.g. a Radix
      // Menu's portaled content) is a legitimate nested overlay — leave it
      // alone, otherwise the menu's onSelect loses focus and never fires.
      const handleFocusIn = (event: FocusEvent) => {
        const { target } = event;
        if (!(target instanceof Element)) {
          return;
        }
        if (wrapperRef.current?.contains(target)) {
          lastFocusedInScope = target;
          return;
        }
        if (focusContext.getTopScope() !== scopeRegistration) {
          return;
        }
        // If focus landed on an element inside an inert subtree, an async
        // operation (e.g. Radix restoring focus to a menu trigger after
        // onSelect fires) placed focus in the now-inert background.
        // Pull it back into this scope.
        if (isInInertSubtree(target)) {
          focusFirstDescendant(wrapperRef.current, focusOptionsRef.current);
          return;
        }
        if (!focusContext.isInsideAnyScope(target)) {
          return;
        }
        focusFirstDescendant(wrapperRef.current, focusOptionsRef.current);
      };
      document.addEventListener('focusin', handleFocusIn, true);

      // Tab cycling: prevent focus from escaping the scope (e.g. to parent
      // frames in an iframe-hosted app) by wrapping Tab/Shift+Tab at the
      // boundaries of the scope. The boundaries have to be derived from what
      // the browser actually tabs to — an element that is merely focusable but
      // never reached (e.g. a `display: none` control) would be a boundary the
      // active element can never match, so the wrap would silently never fire.

      // Set while the browser performs the default action of a Tab key press,
      // so the focusout net below can tell tab navigation apart from focus
      // that was lost because its element got re-rendered away.
      let isTabbing = false;

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key !== 'Tab') {
          return;
        }
        if (focusContext.getTopScope() !== scopeRegistration) {
          return;
        }
        isTabbing = true;
        setTimeout(() => {
          isTabbing = false;
        }, 0);
        const wrapper = wrapperRef.current;
        if (!wrapper) {
          return;
        }
        const first = getFirstTabbableChild(wrapper) as HTMLElement | null;
        const last = getLastTabbableChild(wrapper) as HTMLElement | null;
        if (!first || !last) {
          return;
        }
        if (event.shiftKey) {
          if (document.activeElement === first) {
            event.preventDefault();
            last.focus({ preventScroll: true, ...focusOptionsRef.current });
          }
        } else {
          if (document.activeElement === last) {
            event.preventDefault();
            first.focus({ preventScroll: true, ...focusOptionsRef.current });
          }
        }
      };
      document.addEventListener('keydown', handleKeyDown, true);

      // Safety net for tab stops the tab-order helpers cannot see at all:
      // Chrome makes an overflowing scroll container keyboard focusable while
      // still reporting `tabIndex` as -1, so Tab can step onto it and then out
      // of the scope, dropping focus on `<body>`. A null `relatedTarget` means
      // focus went nowhere, so bring it back to the scope container. Only tab
      // navigation is corrected — focus lost to a re-render belongs to whoever
      // owns that element (e.g. drag and drop restoring its own drag handle).
      const handleFocusOut = (event: FocusEvent) => {
        if (!isTabbing) {
          return;
        }
        if (focusContext.getTopScope() !== scopeRegistration) {
          return;
        }
        if (event.relatedTarget !== null) {
          return;
        }
        requestAnimationFrame(() => {
          const wrapper = wrapperRef.current;
          // Focus left the document altogether (tab switch, dev tools) —
          // stealing it back would fight the browser.
          if (!wrapper || !document.hasFocus()) {
            return;
          }
          if (wrapper.contains(document.activeElement)) {
            return;
          }
          const container = wrapper.firstElementChild as HTMLElement | null;
          container?.focus({ preventScroll: true });
          if (document.activeElement !== container) {
            focusFirstDescendant(wrapper, focusOptionsRef.current);
          }
        });
      };
      document.addEventListener('focusout', handleFocusOut, true);

      focusFirstDescendant(wrapperRef.current, focusOptions);

      // The walk above leaves behind the last element it tried, which is not
      // necessarily one that ever held focus. Only focus moves from here on
      // (e.g. the content focusing its own primary action via a ref) are worth
      // restoring.
      lastFocusedInScope = null;

      // A closing overlay (e.g. Radix Menu) can steal the focus we just set
      // before tearing down its trap, leaving focus on the body. If that
      // happened, claim focus again after the next macrotask, when the overlay
      // is unmounted and its trap is gone.
      let reassertFocusTimeout: ReturnType<typeof setTimeout> | undefined;
      if (!wrapperRef.current?.contains(document.activeElement)) {
        reassertFocusTimeout = setTimeout(() => {
          const wrapper = wrapperRef.current;
          if (
            !wrapper ||
            focusContext.getTopScope() !== scopeRegistration ||
            wrapper.contains(document.activeElement)
          ) {
            return;
          }
          if (
            lastFocusedInScope instanceof HTMLElement &&
            wrapper.contains(lastFocusedInScope) &&
            attemptFocus(lastFocusedInScope, focusOptionsRef.current)
          ) {
            return;
          }
          focusFirstDescendant(wrapper, focusOptionsRef.current);
        }, 0);
      }

      return () => {
        clearTimeout(reassertFocusTimeout);
        document.removeEventListener('focusin', handleFocusIn, true);
        document.removeEventListener('keydown', handleKeyDown, true);
        document.removeEventListener('focusout', handleFocusOut, true);
        unregister();
        focusContext.setBackgroundInertness(false);

        // Only restore focus to this scope's trigger when it would not steal
        // focus from another still-active scope: if a remaining scope already
        // owns focus, leave it alone.
        const stillActive = focusContext.getTopScope();
        if (
          !stillActive?.wrapperRef.current?.contains(document.activeElement)
        ) {
          focusContext.focusTriggerElement(triggerElementCopy);
        }
      };
    }

    if (autoFocus) {
      focusFirstDescendant(wrapperRef.current, focusOptions);
    }

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') {
        return;
      }

      if (e.target === triggerElement.current) {
        e.preventDefault();
        focusFirstDescendant(wrapperRef.current, focusOptionsRef.current);
      }
    };

    if (!autoFocus && !contain) {
      document.addEventListener('keydown', handleTabKey);
    }

    const triggerElementCopy = triggerElement.current;

    return () => {
      document.removeEventListener('keydown', handleTabKey);
      // restore focus on trigger when unmounting the focusscope
      triggerElementCopy.focus();
    };

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [contain, autoFocus]);

  return (
    <div
      style={{ display: 'contents' }}
      ref={wrapperRef}
      aria-labelledby={triggerElement.current.id}
      data-testid={dataTestId}
      // Prevents that focus is lost when clicking on non-focusable items inside (atm used in the internal Drawer).
      // Probably worth considering moving that to the focusScope when redoing the overlays.
      // tabIndex={-1}
    >
      {children}
    </div>
  );
};

FocusScope.displayName = 'FocusScope';
